import {
  createRouter,
  createRoute,
  createRootRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { rememberStravaPublicOnly } from "./lib/strava-public-hint";
import React from "react";
import { queryClient } from "./lib/query-client";
import type { User } from "@atlas/shared";
import { fetchMe } from "./api/auth";
import AuthLayout from "./layouts/AuthLayout";
import AppShell from "./layouts/AppShell";
import PublicShell from "./layouts/PublicShell";
const LoginPage = lazyRouteComponent(() => import("./pages/auth/LoginPage"));
const SignupPage = lazyRouteComponent(() => import("./pages/auth/SignupPage"));
const PartnerLandingPage = lazyRouteComponent(() => import("./pages/PartnerLandingPage"));
const JoinPage = lazyRouteComponent(() => import("./pages/JoinPage"));
const ForgotPasswordPage = lazyRouteComponent(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazyRouteComponent(() => import("./pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazyRouteComponent(() => import("./pages/auth/VerifyEmailPage"));
const WelcomeEmailPage = lazyRouteComponent(() => import("./pages/auth/WelcomeEmailPage"));
const WelcomeAppPage = lazyRouteComponent(() => import("./pages/auth/WelcomeAppPage"));
import { shouldOfferApp } from "./lib/welcome-app";
const DashboardPage = lazyRouteComponent(() => import("./pages/DashboardPage"));
// The Climb landing is the landing page (the pre-2026-09 marketing page was
// deleted 2026-10-09; it lives in git history if ever needed).
const LandingPage = lazyRouteComponent(() => import("./pages/landing/ClimbLandingPage"));

// ── Preview: seeds mock data, bypasses auth ───────────────────────────────────

function DashboardPreview() {
  React.useEffect(() => {
    queryClient.setQueryData(["profile/stats"], {
      stats: {
        outdoorScore: 12_480,
        level: 7,
        levelProgress: 0.63,
        toNextLevel: 3520,
        totalActivities: 214,
        totalDistanceM: 2_340_000,
        totalElevationGainM: 98_000,
        totalSummits: 42,
        totalDaysOut: 178,
        totalCountries: 5,
        updatedAt: null,
      },
      recentUnlocks: [
        { achievementId: "1", unlockedAt: "2026-05-01", slug: "first-munro", name: "First Munro", description: "Summit your first Munro.", tier: "bronze", points: 50, iconName: "mountain" },
        { achievementId: "2", unlockedAt: "2026-04-15", slug: "ten-peaks", name: "Ten Peaks", description: "Summit 10 peaks.", tier: "silver", points: 150, iconName: "mountain" },
        { achievementId: "3", unlockedAt: "2026-03-20", slug: "century-days", name: "100 Days Out", description: "Log 100 days outdoors.", tier: "gold", points: 500, iconName: "sun" },
      ],
      streaks: { currentStreak: 12, longestStreak: 31 },
    });
    queryClient.setQueryData(["profile/activity-heatmap"], {
      data: Array.from({ length: 80 }, (_, i) => ({
        date: new Date(Date.now() - i * 86400000).toISOString().slice(0, 10),
        count: Math.random() > 0.6 ? Math.ceil(Math.random() * 6) : 0,
      })),
    });
    queryClient.setQueryData(["profile/suggestions"], {
      suggestions: [
        { itemType: "summit", id: "s1", name: "Ben Nevis", slug: "ben-nevis", elevation: 1345, latitude: 56.8, longitude: -5.0 },
        { itemType: "summit", id: "s2", name: "Scafell Pike", slug: "scafell-pike", elevation: 978, latitude: 54.5, longitude: -3.2 },
        { itemType: "summit", id: "s3", name: "Snowdon", slug: "snowdon", elevation: 1085, latitude: 53.1, longitude: -4.1 },
        { itemType: "summit", id: "s4", name: "Cairn Gorm", slug: "cairn-gorm", elevation: 1245, latitude: 57.1, longitude: -3.7 },
      ],
    });
    queryClient.setQueryData(["collections"], {
      collections: [
        { id: "c1", slug: "munros", name: "Munros", type: "Peak List", description: null, coverImage: "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=800&q=80", region: "Scotland", country: "GB", itemCount: 282, completedCount: 42, pct: 14.9 },
        { id: "c2", slug: "wainwrights", name: "Wainwrights", type: "Peak List", description: null, coverImage: "https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?auto=format&fit=crop&w=800&q=80", region: "Lake District", country: "GB", itemCount: 214, completedCount: 87, pct: 40.7 },
        { id: "c3", slug: "national-3-peaks", name: "National 3 Peaks", type: "Challenge", description: null, coverImage: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=800&q=80", region: null, country: "GB", itemCount: 3, completedCount: 2, pct: 66.7 },
      ],
    });
    queryClient.setQueryData(["dashboard/showcase"], {
      url: "/preview-map",
      data: {
        owner: { name: "Preview", username: "preview" },
        features: {
          type: "FeatureCollection",
          features: LAKE_DISTRICT_PEAKS.features.filter((f) => f.properties.status === "collected"),
        },
        heatmap: MOCK_HEATMAP,
        equipped: { mapTheme: null, traceStyle: null, pin: null },
      },
    });
  }, []);

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <DashboardPage />
    </div>
  );
}
const MapPage = lazyRouteComponent(() => import("./pages/MapPage"));
import type { MapFeatureCollection, HeatmapFeatureCollection } from "./api/map";
import type { FilterType } from "./components/map/FilterBar";
const CollectionsPage = lazyRouteComponent(() => import("./pages/CollectionsPage"));
const CollectionDetailPage = lazyRouteComponent(() => import("./pages/CollectionDetailPage"));
const MyListsPage = lazyRouteComponent(() => import("./pages/MyListsPage"));
const MyListDetailPage = lazyRouteComponent(() => import("./pages/MyListDetailPage"));
const AchievementsPage = lazyRouteComponent(() => import("./pages/AchievementsPage"));
const RegionPage = lazyRouteComponent(() => import("./pages/RegionPage"));
const TrailDetailPage = lazyRouteComponent(() => import("./pages/TrailDetailPage"));
const RouteAnalyserPage = lazyRouteComponent(() => import("./pages/RouteAnalyserPage"));
const RouteSharePage = lazyRouteComponent(() => import("./pages/RouteSharePage"));
const WhereToStartPage = lazyRouteComponent(() => import("./pages/WhereToStartPage"));
const PartnerPage = lazyRouteComponent(() => import("./pages/PartnerPage"));
const RegionsPage = lazyRouteComponent(() => import("./pages/RegionsPage"));
const ProfilePage = lazyRouteComponent(() => import("./pages/ProfilePage"));
const StatisticsPage = lazyRouteComponent(() => import("./pages/StatisticsPage"));
const ConnectedSourcesPage = lazyRouteComponent(() => import("./pages/ConnectedSourcesPage"));
const PhotoImportPage = lazyRouteComponent(() => import("./pages/PhotoImportPage"));
const PlanPage = lazyRouteComponent(() => import("./pages/PlanPage"));
const ActivityReviewPage = lazyRouteComponent(() => import("./pages/ActivityReviewPage"));
const ActivitiesPage = lazyRouteComponent(() => import("./pages/ActivitiesPage"));
import type { ActivitiesSearch } from "./pages/ActivitiesPage";
import { isActivityKind } from "./lib/activity-kind";
const TripDetailPage = lazyRouteComponent(() => import("./pages/TripDetailPage"));
const TripSharePage = lazyRouteComponent(() => import("./pages/TripSharePage"));
const StorePage = lazyRouteComponent(() => import("./pages/store/StorePage"));
const FeedPage = lazyRouteComponent(() => import("./pages/social/FeedPage"));
const GroupsPage = lazyRouteComponent(() => import("./pages/groups/GroupsPage"));
const GroupPage = lazyRouteComponent(() => import("./pages/groups/GroupPage"));
const GroupJoinPage = lazyRouteComponent(() => import("./pages/groups/GroupJoinPage"));
const ChallengePage = lazyRouteComponent(() => import("./pages/groups/ChallengePage"));
const GroupCollectionPage = lazyRouteComponent(() => import("./pages/groups/GroupCollectionPage"));
const GroupSettingsPage = lazyRouteComponent(() => import("./pages/groups/GroupSettingsPage"));
const ExplorerProfilePage = lazyRouteComponent(() => import("./pages/social/ExplorerProfilePage"));
const ExplorerCollectionPage = lazyRouteComponent(() => import("./pages/social/ExplorerCollectionPage"));
const ShowcasePage = lazyRouteComponent(() => import("./pages/ShowcasePage"));
const ActivitySharePage = lazyRouteComponent(() => import("./pages/ActivitySharePage"));
const AdminOverviewPage = lazyRouteComponent(() => import("./pages/admin/AdminOverviewPage"));
const AdminUsersPage = lazyRouteComponent(() => import("./pages/admin/AdminUsersPage"));
const AdminUsagePage = lazyRouteComponent(() => import("./pages/admin/AdminUsagePage"));
const AdminRevenuePage = lazyRouteComponent(() => import("./pages/admin/AdminRevenuePage"));
const AdminPipelinePage = lazyRouteComponent(() => import("./pages/admin/AdminPipelinePage"));
const AdminFunnelPage = lazyRouteComponent(() => import("./pages/admin/AdminFunnelPage"));
const AdminUserActivitiesPage = lazyRouteComponent(() => import("./pages/admin/AdminUserActivitiesPage"));
const AdminUserProfilePage = lazyRouteComponent(() => import("./pages/admin/AdminUserProfilePage"));
const AdminUserActivityPage = lazyRouteComponent(() => import("./pages/admin/AdminUserActivityPage"));
const AdminStorePage = lazyRouteComponent(() => import("./pages/admin/AdminStorePage"));
const AdminCollectionsPage = lazyRouteComponent(() => import("./pages/admin/AdminCollectionsPage"));
const AdminAchievementsPage = lazyRouteComponent(() => import("./pages/admin/AdminAchievementsPage"));
const AdminEventsPage = lazyRouteComponent(() => import("./pages/admin/AdminEventsPage"));
const AdminSocialPage = lazyRouteComponent(() => import("./pages/admin/AdminSocialPage"));
const AdminGroupsPage = lazyRouteComponent(() => import("./pages/admin/AdminGroupsPage"));
const AdminFeedbackPage = lazyRouteComponent(() => import("./pages/admin/AdminFeedbackPage"));
const FeedbackListPage = lazyRouteComponent(() => import("./pages/FeedbackListPage"));
const FeedbackDetailPage = lazyRouteComponent(() => import("./pages/FeedbackDetailPage"));
const AdminKeysPage = lazyRouteComponent(() => import("./pages/admin/AdminKeysPage"));
const AdminPartnersPage = lazyRouteComponent(() => import("./pages/admin/AdminPartnersPage"));
const AdminReferralsPage = lazyRouteComponent(() => import("./pages/admin/AdminReferralsPage"));
const AdminFlagsPage = lazyRouteComponent(() => import("./pages/admin/AdminFlagsPage"));
const AdminAnnouncementsPage = lazyRouteComponent(() => import("./pages/admin/AdminAnnouncementsPage"));
const AdminEmailsPage = lazyRouteComponent(() => import("./pages/admin/AdminEmailsPage"));
const AdminAuditPage = lazyRouteComponent(() => import("./pages/admin/AdminAuditPage"));
const PeakDetailPage = lazyRouteComponent(() => import("./pages/PeakDetailPage"));
const LandmarkDetailPage = lazyRouteComponent(() => import("./pages/LandmarkDetailPage"));
const PrivacyPage = lazyRouteComponent(() => import("./pages/legal/PrivacyPage"));
const TermsPage = lazyRouteComponent(() => import("./pages/legal/TermsPage"));
const CommunityGuidelinesPage = lazyRouteComponent(() => import("./pages/legal/CommunityGuidelinesPage"));
const CreditsPage = lazyRouteComponent(() => import("./pages/legal/CreditsPage"));
const PressPage = lazyRouteComponent(() => import("./pages/PressPage"));
const WrappedPage = lazyRouteComponent(() => import("./pages/WrappedPage"));
import { featuredWrappedYear } from "./lib/wrapped-season";
import { usePartnerParam } from "./hooks/usePartnerParam";
import { InAppBrowserBanner } from "./components/InAppBrowserBanner";

// ── Root ─────────────────────────────────────────────────────────────────────

function RootComponent() {
  usePartnerParam();
  return (
    <>
      <Outlet />
      <InAppBrowserBanner />
    </>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
});

// ── Index: landing page ───────────────────────────────────────────────────────

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  // Authed visitors go straight to the dashboard; the landing page is
  // marketing for signed-out visitors only. Uses the same cached ["me"]
  // query as the protected layout, so no extra request on navigation.
  // retry: false — this is a session sniff; if the API errors, show the
  // marketing page immediately rather than blocking on retry backoff.
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({
        queryKey: ["me"],
        queryFn: fetchMe,
        retry: false,
      });
    } catch {
      return; // no session — fall through to the landing page
    }
    throw redirect({ to: "/dashboard" });
  },
  component: LandingPage,
});

// ── Explorer public profile (root child — reachable while signed out) ────────

const explorerProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explorers/$username",
  component: ExplorerProfilePage,
});

const explorerCollectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explorers/$username/lists/$id",
  component: ExplorerCollectionPage,
});

// ── Showcase (root child — the shareable full-screen completed-only map;
//    sig-gated server-side, so no auth guard and no shell) ───────────────────

const showcaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/showcase",
  validateSearch: (search: Record<string, unknown>): { u?: string; sig?: string } => ({
    u: typeof search["u"] === "string" ? search["u"] : undefined,
    sig: typeof search["sig"] === "string" ? search["sig"] : undefined,
  }),
  component: function ShowcaseRoute() {
    const { u, sig } = useSearch({ from: "/showcase" });
    return <ShowcasePage userId={u} sig={sig} />;
  },
});

// ── Activity share (root child — public page behind a signed link; the API
//    landing at /share/activity/:id 302s browsers here) ─────────────────────

const activityShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/activity/$id",
  validateSearch: (search: Record<string, unknown>): { sig?: string; i?: string } => ({
    sig: typeof search["sig"] === "string" ? search["sig"] : undefined,
    // TanStack parses bare numerals as numbers (`?i=1` → 1), so stringify.
    i: search["i"] != null ? String(search["i"]) : undefined,
  }),
  component: function ActivityShareRoute() {
    const { id } = useParams({ from: "/share/activity/$id" });
    const { sig, i } = useSearch({ from: "/share/activity/$id" });
    return <ActivitySharePage id={id} sig={sig} identity={i === "1" || i === "true"} />;
  },
});

// ── Trip share (spec 35; root child, public page behind a signed link that
//    the owner can switch off; the API landing at /share/trip/:id 302s
//    browsers here) ────────────────────────────────────────────────────────────

const tripShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/trip/$id",
  validateSearch: (search: Record<string, unknown>): { sig?: string; i?: string } => ({
    sig: typeof search["sig"] === "string" ? search["sig"] : undefined,
    i: search["i"] != null ? String(search["i"]) : undefined,
  }),
  component: function TripShareRoute() {
    const { id } = useParams({ from: "/share/trip/$id" });
    const { sig, i } = useSearch({ from: "/share/trip/$id" });
    return <TripSharePage id={id} sig={sig} identity={i === "1" || i === "true"} />;
  },
});

// ── Peak & landmark detail (root children — SEO surface, reachable signed out;
//    PLT-2) ────────────────────────────────────────────────────────────────

const peakDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/peaks/$slug",
  component: PeakDetailPage,
});

const landmarkDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/landmarks/$slug",
  component: LandmarkDetailPage,
});

// ── Static legal pages (root children — public, PLT-6) ────────────────────────

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

const communityGuidelinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/community-guidelines",
  component: CommunityGuidelinesPage,
});

const creditsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/credits",
  component: CreditsPage,
});

const pressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/press",
  component: PressPage,
});

// ── Auth layout ───────────────────────────────────────────────────────────────

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  // `next`: app path to return to after signing in (route analyser's
  // "Sign in to see what's new to you"). LoginPage only follows same-site
  // paths.
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search["next"] === "string" ? search["next"] : undefined,
  }),
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/signup",
  validateSearch: (search: Record<string, unknown>): { invite?: string; ref?: string } => ({
    invite: typeof search["invite"] === "string" ? search["invite"] : undefined,
    // Referrer username from a /join/{username} link (spec 21).
    ref: typeof search["ref"] === "string" ? search["ref"] : undefined,
  }),
  component: SignupPage,
});

// ── Partner landing (public, noindex — ATL-134) ─────────────────────────────
// /with/{code} is the link creators and press share; /beta/{code} stays for
// the invite links already out there. Same page, same API.

const partnerLandingRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/with/$code",
  component: PartnerLandingPage,
});

const betaInviteRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/beta/$code",
  component: PartnerLandingPage,
});

// ── Invite a friend landing (public, noindex — spec 21 / ATL-96) ─────────────
// May also carry ?invite= so one URL can hold a beta code and a referral.

const joinRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/join/$username",
  validateSearch: (search: Record<string, unknown>): { invite?: string } => ({
    invite: typeof search["invite"] === "string" ? search["invite"] : undefined,
  }),
  component: JoinPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search["token"] === "string" ? search["token"] : undefined,
  }),
  component: ResetPasswordPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search["token"] === "string" ? search["token"] : undefined,
  }),
  component: VerifyEmailPage,
});

// One-off "add your email" step for a brand-new Strava-first account (the
// Strava callback sends it to /dashboard?welcome=email; see the dashboard
// route). Signed-in only; the page itself sends anyone who does not need it
// on to the dashboard.
const welcomeEmailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/welcome/email",
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({ queryKey: ["me"], queryFn: fetchMe });
    } catch {
      throw redirect({ to: "/login" });
    }
  },
  component: WelcomeEmailPage,
});

// One-off "get the app" step for a brand-new account on a phone browser
// (creator links open in Instagram's browser, so that is where they sign up).
// The dashboard route sends them here once; the page re-checks and moves on.
const welcomeAppRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/welcome/app",
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({ queryKey: ["me"], queryFn: fetchMe });
    } catch {
      throw redirect({ to: "/login" });
    }
  },
  component: WelcomeAppPage,
});

// ── Public app layout (SEO surface — collections/regions/trails; PLT-2) ──────
// Reachable while signed out, unlike `_protected`. AppShell for signed-in
// visitors, a slim public header otherwise — see layouts/PublicShell.tsx.
// The beforeLoad only pre-warms the `["me"]` cache (never redirects), same
// pattern as `indexRoute` above.

const publicAppRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_publicApp",
  component: PublicShell,
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({
        queryKey: ["me"],
        queryFn: fetchMe,
        retry: false,
      });
    } catch {
      // no session — PublicShell renders the slim public chrome
    }
  },
});

const collectionsRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/collections",
  component: CollectionsPage,
});

const collectionDetailRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/collections/$slug",
  component: CollectionDetailPage,
});

// Filtered aliases of the collections directory (ATL-132): same page, one
// collection type preselected, own title/canonical, prerendered as static
// HTML (scripts/prerender.mjs writes dist/<alias>/index.html, which is safe
// next to dist/trails/<slug>/ and dist/landmarks/<slug>/ because each folder
// has its own index.html - see the /press note on routeAnalyserRoute).
const hillsRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/hills",
  component: () => <CollectionsPage typeFilter="peaks" />,
});

const trailsIndexRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/trails",
  component: () => <CollectionsPage typeFilter="trails" />,
});

const landmarksIndexRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/landmarks",
  component: () => <CollectionsPage typeFilter="landmarks" />,
});

const regionsListRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/regions",
  component: RegionsPage,
});

const regionRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/regions/$slug",
  component: RegionPage,
});

const trailDetailRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/trails/$id",
  component: TrailDetailPage,
});

// Route analyser (spec 27): public, checks a planned GPX. AppShell when
// signed in, slim public chrome otherwise. No folder of this name may exist
// in packages/web/public (the host would answer S3 403 instead of the SPA).
const routeAnalyserRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/route-analyser",
  component: RouteAnalyserPage,
});

// A shared route check (spec 27 round 3): public, unlisted, noindex, never
// prerendered. Full width signed out, like the analyser (PublicShell).
const routeShareRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/routes/$id",
  component: RouteSharePage,
});

// Group invite (spec 32 / ATL-151). Under _publicApp so a signed-out visitor
// gets the sign-up / sign-in card instead of a bounce to /login; signed in,
// it is the preview + join page inside AppShell. Not prerendered, noindex.
const groupJoinRoute = createRoute({
  getParentRoute: () => publicAppRoute,
  path: "/groups/join/$code",
  validateSearch: (search: Record<string, unknown>): { ref?: string } => ({
    // TanStack parses an all-digit value as a number, so stringify.
    ref: search["ref"] != null && search["ref"] !== "" ? String(search["ref"]) : undefined,
  }),
  component: GroupJoinPage,
});

// ── Protected layout (requires auth) ─────────────────────────────────────────

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_protected",
  component: AppShell,
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({
        queryKey: ["me"],
        queryFn: fetchMe,
      });
    } catch {
      throw redirect({ to: "/login" });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/dashboard",
  // `welcome=email` comes only from the Strava callback that just created
  // the account. Read off the raw search so /dashboard links stay untyped.
  beforeLoad: ({ location }) => {
    // A Strava sign-in that only granted public activities (ATL-173): keep
    // the hint for the Strava card on Connected sources.
    if ((location.search as Record<string, unknown>)["strava_scope"] === "public") {
      rememberStravaPublicOnly();
    }
    if ((location.search as Record<string, unknown>)["welcome"] === "email") {
      throw redirect({ to: "/welcome/email", replace: true });
    }
    // Every signup path (email, plan step, Apple/Google, Strava) ends here,
    // so this is the one place to offer the app to a new phone signup.
    const me = queryClient.getQueryData<{ user: User }>(["me"]);
    if (shouldOfferApp(me?.user)) {
      throw redirect({ to: "/welcome/app", replace: true });
    }
  },
  component: DashboardPage,
});

const mapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/map",
  validateSearch: (search: Record<string, unknown>): {
    collection?: string;
    region?: string;
    trail?: string;
    filter?: string;
    focus?: string;
  } => ({
    collection: typeof search["collection"] === "string" ? search["collection"] : undefined,
    region: typeof search["region"] === "string" ? search["region"] : undefined,
    trail: typeof search["trail"] === "string" ? search["trail"] : undefined,
    filter: typeof search["filter"] === "string" ? search["filter"] : undefined,
    focus: typeof search["focus"] === "string" ? search["focus"] : undefined,
  }),
  component: function MapRoute() {
    const { filter, collection, region, trail, focus } = useSearch({ from: "/_protected/map" });
    const validFilters: FilterType[] = ["all", "peaks", "trails", "landmarks", "gaps", "completed", "none"];
    const initialFilter = validFilters.includes(filter as FilterType) ? (filter as FilterType) : "all";
    return (
      <MapPage
        initialFilter={initialFilter}
        collectionSlug={collection}
        regionSlug={region}
        trailId={trail}
        focusId={focus}
      />
    );
  },
});

const myListsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/lists",
  component: MyListsPage,
});

const myListDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/lists/$id",
  component: MyListDetailPage,
});

const achievementsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/achievements",
  component: AchievementsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfilePage,
});

// Where to start (spec 31 / ATL-146): rank stations and car parks by the hills
// left on the chosen lists. Every param is optional so a view is linkable.
export interface WhereToStartSearchParams {
  lists?: string;
  loop?: number;
  mode?: "lobe" | "centred";
  start?: string;
  lat?: number;
  lng?: number;
  place?: string;
  bearing?: number;
  /** Focus radius round the origin in km (default 7.5). */
  r?: number;
  type?: "station" | "car_park";
}

const optNum = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};
const optStr = (v: unknown): string | undefined =>
  typeof v === "string" && v !== "" ? v : typeof v === "number" ? String(v) : undefined;

const whereToStartRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/where-to-start",
  validateSearch: (search: Record<string, unknown>): WhereToStartSearchParams => ({
    // "" is meaningful here: every list switched off.
    lists: typeof search["lists"] === "string" ? search["lists"] : undefined,
    loop: optNum(search["loop"]),
    mode: search["mode"] === "centred" ? "centred" : search["mode"] === "lobe" ? "lobe" : undefined,
    start: optStr(search["start"]),
    lat: optNum(search["lat"]),
    lng: optNum(search["lng"]),
    place: optStr(search["place"]),
    bearing: optNum(search["bearing"]),
    r: optNum(search["r"]),
    type: search["type"] === "station" || search["type"] === "car_park" ? search["type"] : undefined,
  }),
  component: WhereToStartPage,
});

const partnerRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/partner",
  component: PartnerPage,
});

const statisticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/statistics",
  component: StatisticsPage,
});

// Activity list with source filter + pagination; both live in the URL so
// Connected sources can deep-link a pre-filtered view.
const activitiesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/activities",
  validateSearch: (search: Record<string, unknown>): ActivitiesSearch => {
    const page = Number(search["page"]);
    return {
      source: typeof search["source"] === "string" && search["source"] ? search["source"] : undefined,
      kind: isActivityKind(search["kind"]) ? search["kind"] : undefined,
      page: Number.isInteger(page) && page > 1 ? page : undefined,
      select: search["select"] === 1 || search["select"] === "1" || search["select"] === true ? 1 : undefined,
      removed: search["removed"] === 1 || search["removed"] === "1" || search["removed"] === true ? 1 : undefined,
    };
  },
  component: ActivitiesPage,
});

// Trip page (spec 35): several activities as one walk.
const tripDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/trips/$tripId",
  component: TripDetailPage,
});

// Bare /wrapped redirects to the featured year — the story page itself lives
// at /wrapped/$year (PLT-4). The featured year is the year that's ending, so
// the January half of the season still reviews the year just gone.
const wrappedIndexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/wrapped",
  beforeLoad: () => {
    throw redirect({
      to: "/wrapped/$year",
      params: { year: String(featuredWrappedYear()) },
    });
  },
});

const wrappedYearRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/wrapped/$year",
  component: WrappedPage,
});

const activityReviewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/activities/$id/review",
  component: ActivityReviewPage,
});

const connectedSourcesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/connected-sources",
  component: ConnectedSourcesPage,
});

// Bulk photo check-ins (spec 39 / ATL-166).
const photoImportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/add-from-photos",
  component: PhotoImportPage,
});

const storeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/store",
  validateSearch: (search: Record<string, unknown>): { order?: string; status?: string } => ({
    order: typeof search["order"] === "string" ? search["order"] : undefined,
    status: typeof search["status"] === "string" ? search["status"] : undefined,
  }),
  component: StorePage,
});

const feedRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/feed",
  validateSearch: (search: Record<string, unknown>): { attach?: string } => ({
    attach: typeof search["attach"] === "string" ? search["attach"] : undefined,
  }),
  component: FeedPage,
});

// Groups (spec 32 / ATL-151): private boards and challenges. Private pages,
// never prerendered. The join page lives under _publicApp (see above).
const groupsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/groups",
  component: GroupsPage,
});

const groupRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/groups/$id",
  // ?tab=updates opens the feed (group post notifications link there).
  validateSearch: (search: Record<string, unknown>): { tab?: "standings" | "updates" } => ({
    tab: search["tab"] === "updates" || search["tab"] === "standings" ? search["tab"] : undefined,
  }),
  component: GroupPage,
});

const groupChallengeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/groups/$id/challenges/$cid",
  component: ChallengePage,
});

const groupCollectionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/groups/$id/collections/$slug",
  component: GroupCollectionPage,
});

const groupSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/groups/$id/settings",
  component: GroupSettingsPage,
});

const planRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/plan",
  validateSearch: (search: Record<string, unknown>): { checkout?: string; product?: string } => ({
    checkout: typeof search["checkout"] === "string" ? search["checkout"] : undefined,
    product: typeof search["product"] === "string" ? search["product"] : undefined,
  }),
  component: PlanPage,
});

const adminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin",
  component: AdminOverviewPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/users",
  // ?q= pre-fills the search box so other admin tabs can link to one user.
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search["q"] === "string" ? search["q"] : undefined,
  }),
  component: AdminUsersPage,
});

const adminUsageRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/usage",
  component: AdminUsagePage,
});

const adminRevenueRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/revenue",
  component: AdminRevenuePage,
});

const adminFunnelRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/funnel",
  component: AdminFunnelPage,
});

const adminPipelineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/pipeline",
  component: AdminPipelinePage,
});

const adminUserProfileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/users/$userId",
  component: AdminUserProfilePage,
});

const adminUserActivitiesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/users/$userId/activities",
  component: AdminUserActivitiesPage,
});

const adminUserActivityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/users/$userId/activities/$activityId",
  component: AdminUserActivityPage,
});

const adminStoreRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/store",
  component: AdminStorePage,
});

const adminCollectionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/collections",
  component: AdminCollectionsPage,
});

const adminAchievementsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/achievements",
  component: AdminAchievementsPage,
});

const adminEventsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/events",
  component: AdminEventsPage,
});

const adminSocialRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/social",
  component: AdminSocialPage,
});

const adminGroupsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/groups",
  component: AdminGroupsPage,
});

const adminFeedbackRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/feedback",
  component: AdminFeedbackPage,
});

// The user's own feedback threads. Reply notifications (kind feedback_reply)
// and the reply email both link to /feedback/:id.
const feedbackListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/feedback",
  component: FeedbackListPage,
});

const feedbackDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/feedback/$id",
  component: FeedbackDetailPage,
});

const adminKeysRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/keys",
  component: AdminKeysPage,
});

const adminPartnersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/partners",
  component: AdminPartnersPage,
});

// Old bookmark; same page.
const adminBetaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/beta",
  component: AdminPartnersPage,
});

const adminReferralsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/referrals",
  component: AdminReferralsPage,
});

const adminFlagsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/flags",
  component: AdminFlagsPage,
});

const adminAnnouncementsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/announcements",
  component: AdminAnnouncementsPage,
});

const adminEmailsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/emails",
  component: AdminEmailsPage,
});

const adminAuditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/audit",
  component: AdminAuditPage,
});

// ── Preview route (dev-only) ──────────────────────────────────────────────────

const previewDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-dashboard",
  component: DashboardPreview,
});

const LAKE_DISTRICT_PEAKS: MapFeatureCollection = {
  type: "FeatureCollection",
  features: [
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.007, 54.527] }, properties: { id: "p1", name: "Helvellyn", slug: "helvellyn", type: "peak", status: "collected", elevation: 950 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.211, 54.454] }, properties: { id: "p2", name: "Scafell Pike", slug: "scafell-pike", type: "peak", status: "collected", elevation: 978 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.147, 54.654] }, properties: { id: "p3", name: "Skiddaw", slug: "skiddaw", type: "peak", status: "collected", elevation: 931 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.221, 54.482] }, properties: { id: "p4", name: "Great Gable", slug: "great-gable", type: "peak", status: "collected", elevation: 899 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-2.918, 54.530] }, properties: { id: "p5", name: "Fairfield", slug: "fairfield", type: "peak", status: "collected", elevation: 873 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.156, 54.557] }, properties: { id: "p6", name: "Catbells", slug: "catbells", type: "peak", status: "collected", elevation: 451 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.166, 54.468] }, properties: { id: "p7", name: "Bowfell", slug: "bowfell", type: "peak", status: "not_collected", elevation: 902 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.064, 54.639] }, properties: { id: "p8", name: "Blencathra", slug: "blencathra", type: "peak", status: "not_collected", elevation: 868 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.119, 54.370] }, properties: { id: "p9", name: "Old Man of Coniston", slug: "old-man-of-coniston", type: "peak", status: "not_collected", elevation: 803 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.250, 54.509] }, properties: { id: "p10", name: "Haystacks", slug: "haystacks", type: "peak", status: "not_collected", elevation: 597 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.040, 54.470] }, properties: { id: "p11", name: "Red Screes", slug: "red-screes", type: "peak", status: "not_collected", elevation: 776 } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-2.976, 54.491] }, properties: { id: "p12", name: "St Sunday Crag", slug: "st-sunday-crag", type: "peak", status: "not_collected", elevation: 841 } },
    // Trails
    { type: "Feature", geometry: { type: "LineString", coordinates: [[-2.918, 54.530], [-3.007, 54.527], [-3.064, 54.639], [-3.147, 54.654]] }, properties: { id: "t1", name: "Fairfield Horseshoe", slug: "fairfield-horseshoe", type: "trail", status: "collected", distanceKm: 17.5 } },
    { type: "Feature", geometry: { type: "LineString", coordinates: [[-3.221, 54.482], [-3.211, 54.454], [-3.166, 54.468], [-3.250, 54.509]] }, properties: { id: "t2", name: "Scafell Circuit", slug: "scafell-circuit", type: "trail", status: "in_progress", distanceKm: 13.2 } },
    { type: "Feature", geometry: { type: "LineString", coordinates: [[-3.119, 54.370], [-3.166, 54.468], [-3.250, 54.509]] }, properties: { id: "t3", name: "Coniston Round", slug: "coniston-round", type: "trail", status: "not_collected", distanceKm: 14.0 } },
    // Landmarks
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.098, 54.603] }, properties: { id: "l1", name: "Castlerigg Stone Circle", slug: "castlerigg", type: "landmark", status: "collected", category: "stone_circle" } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.057, 54.567] }, properties: { id: "l2", name: "Ashness Bridge", slug: "ashness-bridge", type: "landmark", status: "collected", category: "bridge" } },
    { type: "Feature", geometry: { type: "Point", coordinates: [-3.220, 54.500] }, properties: { id: "l3", name: "Black Sail Hut", slug: "black-sail-hut", type: "landmark", status: "not_collected", category: "bothy" } },
  ],
};

const MOCK_HEATMAP: HeatmapFeatureCollection = {
  type: "FeatureCollection",
  features: [
    { type: "Feature", geometry: { type: "LineString", coordinates: [[-3.007, 54.527], [-2.990, 54.520], [-2.975, 54.515], [-2.918, 54.530]] }, properties: { id: "a1", name: "Fairfield via Striding Edge", startedAt: "2026-05-10" } },
    { type: "Feature", geometry: { type: "LineString", coordinates: [[-3.147, 54.654], [-3.156, 54.640], [-3.156, 54.557], [-3.098, 54.603]] }, properties: { id: "a2", name: "Skiddaw to Catbells", startedAt: "2026-04-20" } },
    { type: "Feature", geometry: { type: "LineString", coordinates: [[-3.221, 54.482], [-3.211, 54.454], [-3.200, 54.460]] }, properties: { id: "a3", name: "Scafell Pike via Wasdale", startedAt: "2026-03-15" } },
  ],
};

function MapPreview() {
  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <MapPage staticFeatures={LAKE_DISTRICT_PEAKS} staticHeatmap={MOCK_HEATMAP} />
      </div>
    </div>
  );
}

const previewMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-map",
  component: MapPreview,
});

// ── Collections preview ───────────────────────────────────────────────────────

const MOCK_WAINWRIGHTS_ITEMS = [
  { id: "p1", name: "Helvellyn", slug: "helvellyn", elevation: 950, latitude: 54.527, longitude: -3.007, wikiUrl: null, collected: true, visits: 3, lastVisit: "2026-05-10" },
  { id: "p2", name: "Scafell Pike", slug: "scafell-pike", elevation: 978, latitude: 54.454, longitude: -3.211, wikiUrl: null, collected: true, visits: 2, lastVisit: "2026-04-20" },
  { id: "p3", name: "Skiddaw", slug: "skiddaw", elevation: 931, latitude: 54.654, longitude: -3.147, wikiUrl: null, collected: true, visits: 1, lastVisit: "2026-03-15" },
  { id: "p4", name: "Great Gable", slug: "great-gable", elevation: 899, latitude: 54.482, longitude: -3.221, wikiUrl: null, collected: true, visits: 2, lastVisit: "2026-05-01" },
  { id: "p5", name: "Fairfield", slug: "fairfield", elevation: 873, latitude: 54.530, longitude: -2.918, wikiUrl: null, collected: true, visits: 1, lastVisit: "2026-02-28" },
  { id: "p6", name: "Catbells", slug: "catbells", elevation: 451, latitude: 54.557, longitude: -3.156, wikiUrl: null, collected: true, visits: 4, lastVisit: "2026-04-10" },
  { id: "p7", name: "Bowfell", slug: "bowfell", elevation: 902, latitude: 54.468, longitude: -3.166, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p8", name: "Blencathra", slug: "blencathra", elevation: 868, latitude: 54.639, longitude: -3.064, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p9", name: "Old Man of Coniston", slug: "old-man-of-coniston", elevation: 803, latitude: 54.370, longitude: -3.119, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p10", name: "Haystacks", slug: "haystacks", elevation: 597, latitude: 54.509, longitude: -3.250, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p11", name: "Red Screes", slug: "red-screes", elevation: 776, latitude: 54.470, longitude: -3.040, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p12", name: "St Sunday Crag", slug: "st-sunday-crag", elevation: 841, latitude: 54.491, longitude: -2.976, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p13", name: "High Stile", slug: "high-stile", elevation: 806, latitude: 54.515, longitude: -3.289, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p14", name: "Pillar", slug: "pillar", elevation: 892, latitude: 54.495, longitude: -3.278, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p15", name: "Grasmoor", slug: "grasmoor", elevation: 852, latitude: 54.580, longitude: -3.267, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p16", name: "Sail", slug: "sail", elevation: 773, latitude: 54.576, longitude: -3.240, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p17", name: "Wandope", slug: "wandope", elevation: 772, latitude: 54.576, longitude: -3.255, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
  { id: "p18", name: "Eel Crag", slug: "eel-crag", elevation: 839, latitude: 54.574, longitude: -3.249, wikiUrl: null, collected: false, visits: 0, lastVisit: null },
];

const MOCK_COLLECTIONS_DATA = {
  collections: [
    { id: "c1", slug: "wainwrights", name: "Wainwrights", type: "Peak List", description: "214 fells in the Lake District documented by Alfred Wainwright", coverImage: "https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?auto=format&fit=crop&w=800&q=80", region: "Lake District", country: "GB", itemCount: 214, completedCount: 87, pct: 40.7 },
    { id: "c2", slug: "munros", name: "Munros", type: "Peak List", description: "282 Scottish mountains over 3,000 ft", coverImage: "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=800&q=80", region: "Scotland", country: "GB", itemCount: 282, completedCount: 42, pct: 14.9 },
    { id: "c3", slug: "national-3-peaks", name: "National 3 Peaks", type: "Challenge", description: null, coverImage: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=800&q=80", region: null, country: "GB", itemCount: 3, completedCount: 2, pct: 66.7 },
    { id: "c4", slug: "lake-district-landmarks", name: "Lake District Landmarks", type: "Landmarks", description: null, coverImage: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=800&q=80", region: "Lake District", country: "GB", itemCount: 45, completedCount: 18, pct: 40.0 },
    { id: "c5", slug: "pennine-way", name: "Pennine Way", type: "National Trail", description: "268 miles from Edale to Kirk Yetholm", coverImage: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=800&q=80", region: "Northern England", country: "GB", itemCount: 24, completedCount: 9, pct: 37.5 },
  ],
};

function CollectionsPreview() {
  React.useEffect(() => {
    queryClient.setQueryData(["collections"], MOCK_COLLECTIONS_DATA);
    queryClient.setQueryData(["collections", "wainwrights", "items", 0], {
      collection: { id: "c1", slug: "wainwrights", name: "Wainwrights", type: "Peak List", description: null, cover_image: "https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?auto=format&fit=crop&w=800&q=80", region: "Lake District", country: "GB" },
      items: MOCK_WAINWRIGHTS_ITEMS,
    });
  }, []);

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <CollectionsPage />
    </div>
  );
}

function CollectionsDetailPreview() {
  React.useEffect(() => {
    queryClient.setQueryData(["collections"], MOCK_COLLECTIONS_DATA);
    queryClient.setQueryData(["collections", "wainwrights", "items", 0], {
      collection: { id: "c1", slug: "wainwrights", name: "Wainwrights", type: "Peak List", description: null, cover_image: "https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?auto=format&fit=crop&w=800&q=80", region: "Lake District", country: "GB" },
      items: MOCK_WAINWRIGHTS_ITEMS,
    });
  }, []);

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <CollectionDetailPage staticSlug="wainwrights" />
    </div>
  );
}

const previewCollectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-collections",
  component: CollectionsPreview,
});

const previewCollectionsDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-collections-detail",
  component: CollectionsDetailPreview,
});

// ── Achievements preview ──────────────────────────────────────────────────────

const MOCK_ACHIEVEMENTS = {
  achievements: [
    { id: "1", slug: "skyliner", name: "Skyliner", description: "Climb three 800m summits in one day", tier: "gold" as const, points: 500, iconName: "mountain", category: "Summits", unlocked: true, unlockedAt: "2026-04-20", progress: null, rarity: 6.2 },
    { id: "2", slug: "into-thin-air", name: "Into Thin Air", description: "Reach your first 1000m summit", tier: "silver" as const, points: 250, iconName: "flag", category: "Summits", unlocked: true, unlockedAt: "2023-06-15", progress: null, rarity: 41.0 },
    { id: "3", slug: "end-to-end", name: "End to End", description: "Complete a National Trail", tier: "gold" as const, points: 500, iconName: "route", category: "Trails", unlocked: true, unlockedAt: "2025-09-01", progress: null, rarity: 8.7 },
    { id: "4", slug: "the-long-way", name: "The Long Way", description: "Hike 50km in a single day", tier: "platinum" as const, points: 750, iconName: "footprints", category: "Endurance", unlocked: true, unlockedAt: "2025-08-10", progress: null, rarity: 1.4 },
    { id: "5", slug: "dawn-patrol", name: "Dawn Patrol", description: "Log five sunrise summits", tier: "silver" as const, points: 250, iconName: "sunrise", category: "Summits", unlocked: true, unlockedAt: "2024-07-22", progress: null, rarity: 19.3 },
    { id: "6", slug: "cartographer", name: "Cartographer", description: "Visit 25 trig pillars", tier: "bronze" as const, points: 100, iconName: "triangle", category: "Landmarks", unlocked: true, unlockedAt: "2024-02-14", progress: null, rarity: 33.5 },
    { id: "7", slug: "centurion", name: "Centurion", description: "Visit 100 unique summits", tier: "silver" as const, points: 250, iconName: "mountain", category: "Summits", unlocked: false, unlockedAt: null, progress: { value: 78, max: 100 }, rarity: 14.8 },
    { id: "8", slug: "local-legend", name: "Local Legend", description: "Explore 90% of a single region", tier: "gold" as const, points: 500, iconName: "compass", category: "Coverage", unlocked: false, unlockedAt: null, progress: { value: 68, max: 90 }, rarity: 5.1 },
    { id: "9", slug: "all-weather", name: "All Weather", description: "Summit in snow, rain and sun", tier: "bronze" as const, points: 100, iconName: "cloud-rain", category: "Summits", unlocked: false, unlockedAt: null, progress: { value: 2, max: 3 }, rarity: 22.0 },
    { id: "10", slug: "completionist", name: "Completionist", description: "Finish an entire collection", tier: "platinum" as const, points: 750, iconName: "award", category: "Coverage", unlocked: false, unlockedAt: null, progress: { value: 1, max: 3 }, rarity: 2.3 },
    { id: "11", slug: "nightfall", name: "Nightfall", description: "Complete a walk after dark", tier: "bronze" as const, points: 100, iconName: "moon", category: "Endurance", unlocked: false, unlockedAt: null, progress: null, rarity: 28.9 },
    { id: "12", slug: "globetrotter", name: "Globetrotter", description: "Summit peaks in five countries", tier: "gold" as const, points: 500, iconName: "globe", category: "Coverage", unlocked: false, unlockedAt: null, progress: { value: 4, max: 5 }, rarity: 3.6 },
  ],
};

function AchievementsPreview() {
  React.useEffect(() => {
    queryClient.setQueryData(["achievements"], MOCK_ACHIEVEMENTS);
  }, []);
  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <AchievementsPage />
    </div>
  );
}

const previewAchievementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-achievements",
  component: AchievementsPreview,
});

// ── Region preview ────────────────────────────────────────────────────────────

const MOCK_REGION_DATA = {
  id: "r-lake-district",
  name: "Lake District",
  slug: "lake-district",
  subtitle: "National Park · Cumbria, England",
  description:
    "England's largest national park, a landscape of glacial lakes and craggy fells that shaped modern hillwalking and gave Wainwright his life's work.",
  coveragePct: 68,
  heroImage: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1280&q=80",
  geojson: null,
  stats: {
    peaks:     { value: 121, total: 214 },
    trails:    { value: 9,   total: 13  },
    landmarks: { value: 54,  total: 96  },
    distanceKm: 1284,
  },
  collections: [
    { name: "Wainwrights",       value: 121, max: 214, color: "gold"   as const },
    { name: "Birketts",          value: 203, max: 541, color: "sky"    as const },
    { name: "Lakeland waterfalls", value: 18, max: 42,  color: "sky"   as const },
    { name: "Bothies & shelters", value: 4,  max: 9,   color: "spruce" as const },
    { name: "Lake circuits",      value: 6,  max: 6,   color: "gold"   as const },
  ],
  missingNearby: [
    { id: "p1", name: "Bowfell",              slug: "bowfell",              elevationM: 902, collected: false, image: "https://images.unsplash.com/photo-1458668383970-8ddd3927deed?auto=format&fit=crop&w=240&q=80" },
    { id: "p2", name: "Blencathra",           slug: "blencathra",           elevationM: 868, collected: false, image: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=240&q=80" },
    { id: "p3", name: "Haystacks",            slug: "haystacks",            elevationM: 597, collected: false, image: "https://images.unsplash.com/photo-1472791108553-c9405341e398?auto=format&fit=crop&w=240&q=80" },
    { id: "p4", name: "Old Man of Coniston",  slug: "old-man-of-coniston",  elevationM: 803, collected: false, image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=240&q=80" },
  ],
  gapCount: 93,
};

function RegionPreview() {
  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <RegionPage staticSlug="lake-district" staticData={MOCK_REGION_DATA} />
    </div>
  );
}

const previewRegionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-region",
  component: RegionPreview,
});

// ── Trail detail preview ──────────────────────────────────────────────────────

const MOCK_TRAIL_DATA = {
  trail: {
    id: "t-pennine-way",
    name: "Pennine Way",
    slug: "pennine-way",
    country: "England",
    region: "Northern England",
    distanceKm: 431,
    elevationGainM: 11000,
    description: "The original long-distance National Trail, running the length of the Pennines.",
    totalSections: 12,
    completedSections: 4,
    completionPct: 33,
  },
  sections: [
    { id: "s1", sectionNumber: 1, name: "Edale to Crowden", distanceKm: 26, completed: true, completedAt: "2026-03-02", activityId: "a1", coveragePct: 100 },
    { id: "s2", sectionNumber: 2, name: "Crowden to Standedge", distanceKm: 18, completed: true, completedAt: "2026-03-09", activityId: "a2", coveragePct: 100 },
    { id: "s3", sectionNumber: 3, name: "Standedge to Hebden Bridge", distanceKm: 23, completed: true, completedAt: "2026-04-05", activityId: "a3", coveragePct: 100 },
    { id: "s4", sectionNumber: 4, name: "Hebden Bridge to Ickornshaw", distanceKm: 24, completed: true, completedAt: "2026-04-19", activityId: "a4", coveragePct: 100 },
    { id: "s5", sectionNumber: 5, name: "Ickornshaw to Malham", distanceKm: 27, completed: false, completedAt: null, activityId: null, coveragePct: 62 },
    { id: "s6", sectionNumber: 6, name: "Malham to Horton-in-Ribblesdale", distanceKm: 24, completed: false, completedAt: null, activityId: null, coveragePct: 0 },
    { id: "s7", sectionNumber: 7, name: "Horton to Hawes", distanceKm: 22, completed: false, completedAt: null, activityId: null, coveragePct: 0 },
    { id: "s8", sectionNumber: 8, name: "Hawes to Tan Hill", distanceKm: 26, completed: false, completedAt: null, activityId: null, coveragePct: 0 },
  ],
  geojson: {
    type: "FeatureCollection" as const,
    features: [
      { type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[-1.812, 53.383], [-1.913, 53.523]] as [number, number][] }, properties: { sectionId: "s1", status: "collected" as const } },
      { type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[-1.913, 53.523], [-1.998, 53.641]] as [number, number][] }, properties: { sectionId: "s2", status: "collected" as const } },
      { type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[-1.998, 53.641], [-2.078, 53.744]] as [number, number][] }, properties: { sectionId: "s3", status: "collected" as const } },
      { type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[-2.078, 53.744], [-2.114, 53.897]] as [number, number][] }, properties: { sectionId: "s4", status: "collected" as const } },
      { type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[-2.114, 53.897], [-2.153, 54.056]] as [number, number][] }, properties: { sectionId: "s5", status: "not_collected" as const } },
      { type: "Feature" as const, geometry: { type: "LineString" as const, coordinates: [[-2.153, 54.056], [-2.291, 54.150]] as [number, number][] }, properties: { sectionId: "s6", status: "not_collected" as const } },
    ],
  },
  rating: { average: 4.4, count: 12, mine: null, canRate: false },
};

function TrailDetailPreview() {
  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <TrailDetailPage staticId="t-pennine-way" staticData={MOCK_TRAIL_DATA} />
    </div>
  );
}

const previewTrailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-trail",
  component: TrailDetailPreview,
});

// ── Landmark detail preview (check-in capture) ────────────────────────────────
// Dungeon Ghyll Force, matching the waterfalls seed CSV (the July footage
// location - originally mislabelled as Taylorgill Force, a different fall
// above Seathwaite). The visit is seeded as already logged by the Langdale
// Pikes activity so the hero reads "Visited" - the capture then demonstrates
// the photo check-in on top. Network calls the check-in dialog makes are
// stubbed by Playwright in tools/reels.

const MOCK_LANDMARK_DATA = {
  landmark: {
    id: "12d3ac2a-12f8-45a7-83f7-1c5ea3a4c500",
    name: "Dungeon Ghyll Force",
    slug: "dungeon-ghyll-force",
    category: "waterfall",
    latitude: 54.4496,
    longitude: -3.09642,
    region: "Lake District",
    country: "England",
    description:
      "A 12 m fall hidden in the ravine of Dungeon Ghyll below Harrison Stickle, a short detour off the Stickle Tarn path out of Great Langdale.",
  },
  peak: null,
  visits: [
    { id: "v1", visitedAt: "2026-07-18", activityId: "act-1", activityName: "Langdale Pikes round" },
  ],
  collections: [
    { slug: "waterfalls", name: "Waterfalls", itemCount: 150, completedCount: 23 },
  ],
  nearbyUncollected: [
    { id: "n1", name: "Great Langdale Bunkhouse", slug: "great-langdale-bunkhouse", category: "mountain_hut", distanceKm: 0.4 },
    { id: "n2", name: "Bishop's Scale Hut", slug: "bishops-scale-hut", category: "mountain_hut", distanceKm: 1.3 },
    { id: "n3", name: "High Raise", slug: "high-raise", category: "trig_point", distanceKm: 3.1 },
    { id: "n4", name: "Slater Bridge", slug: "slater-bridge", category: "bridge", distanceKm: 4.2 },
    { id: "n5", name: "Cathedral Cave", slug: "cathedral-cave", category: "cave", distanceKm: 4.4 },
  ],
};

// Loaded with the landmark page's own chunk (route splitting, spec 40 review).
const LandmarkPreview = lazyRouteComponent(async () => {
  const [{ LandmarkContent }, { PUBLIC_PAGE_CSS }] = await Promise.all([
    import("./pages/LandmarkDetailPage"),
    import("./components/peak/public-page"),
  ]);
  function Preview() {
    return (
      <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)", overflow: "auto" }}>
        <style>{PUBLIC_PAGE_CSS}</style>
        <LandmarkContent data={MOCK_LANDMARK_DATA as Parameters<typeof LandmarkContent>[0]["data"]} isAuthenticated />
      </div>
    );
  }
  return { default: Preview };
});

const previewLandmarkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-landmark",
  component: LandmarkPreview,
});

// ── Regions list preview ──────────────────────────────────────────────────────

const MOCK_REGIONS_DATA = {
  regions: [
    { id: "r1", name: "Lake District",      slug: "lake-district",      country: "GB", coveragePct: 68, heroImage: "https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?auto=format&fit=crop&w=800&q=80" },
    { id: "r2", name: "Scottish Highlands", slug: "scottish-highlands", country: "GB", coveragePct: 41, heroImage: "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=800&q=80" },
    { id: "r3", name: "Snowdonia",          slug: "snowdonia",          country: "GB", coveragePct: 29, heroImage: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=800&q=80" },
    { id: "r4", name: "Peak District",      slug: "peak-district",      country: "GB", coveragePct: 15 },
    { id: "r5", name: "Cairngorms",         slug: "cairngorms",         country: "GB", coveragePct: 8  },
    { id: "r6", name: "Dartmoor",           slug: "dartmoor",           country: "GB", coveragePct: 0  },
  ],
};

function RegionsPreview() {
  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-app)" }}>
      <RegionsPage staticData={MOCK_REGIONS_DATA} />
    </div>
  );
}

const previewRegionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview-regions",
  component: RegionsPreview,
});

// ── Route tree ────────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  indexRoute,
  explorerProfileRoute,
  explorerCollectionRoute,
  showcaseRoute,
  activityShareRoute,
  tripShareRoute,
  peakDetailRoute,
  landmarkDetailRoute,
  privacyRoute,
  termsRoute,
  communityGuidelinesRoute,
  creditsRoute,
  pressRoute,
  authLayoutRoute.addChildren([
    loginRoute,
    signupRoute,
    partnerLandingRoute,
    betaInviteRoute,
    joinRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
    welcomeEmailRoute,
    welcomeAppRoute,
  ]),
  publicAppRoute.addChildren([
    collectionsRoute,
    collectionDetailRoute,
    hillsRoute,
    trailsIndexRoute,
    landmarksIndexRoute,
    regionsListRoute,
    regionRoute,
    trailDetailRoute,
    routeAnalyserRoute,
    routeShareRoute,
    groupJoinRoute,
  ]),
  protectedRoute.addChildren([
    dashboardRoute,
    mapRoute,
    whereToStartRoute,
    partnerRoute,
    myListsRoute,
    myListDetailRoute,
    achievementsRoute,
    profileRoute,
    statisticsRoute,
    activitiesRoute,
    tripDetailRoute,
    wrappedIndexRoute,
    wrappedYearRoute,
    activityReviewRoute,
    connectedSourcesRoute,
    photoImportRoute,
    storeRoute,
    feedRoute,
    groupsRoute,
    groupRoute,
    groupChallengeRoute,
    groupCollectionRoute,
    groupSettingsRoute,
    planRoute,
    adminRoute,
    adminUsersRoute,
    adminUsageRoute,
    adminRevenueRoute,
    adminPipelineRoute,
    adminFunnelRoute,
    adminUserProfileRoute,
    adminUserActivitiesRoute,
    adminUserActivityRoute,
    adminStoreRoute,
    adminCollectionsRoute,
    adminAchievementsRoute,
    adminEventsRoute,
    adminSocialRoute,
    adminGroupsRoute,
    adminFeedbackRoute,
    feedbackListRoute,
    feedbackDetailRoute,
    adminKeysRoute,
    adminPartnersRoute,
    adminBetaRoute,
    adminReferralsRoute,
    adminFlagsRoute,
    adminAnnouncementsRoute,
    adminEmailsRoute,
    adminAuditRoute,
  ]),
  previewDashboardRoute,
  previewMapRoute,
  previewCollectionsRoute,
  previewCollectionsDetailRoute,
  previewAchievementsRoute,
  previewRegionRoute,
  previewRegionsRoute,
  previewTrailRoute,
  previewLandmarkRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
